import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MiningRoute } from '../../types';

interface ProcessDetailData {
  corePrinciple: string;
  keyPoints: string[];
  technicalPoints: string[];
  comparison?: {
    title: string;
    headers: string[];
    rows: string[][];
  };
  notes?: string[];
}

const processDetailsMap: Record<string, ProcessDetailData> = {
  'iron-ore': {
    corePrinciple: '铁矿石(Fe₂O₃/Fe₃O₄) → 选矿富集 → 烧结/球团 → 高炉还原 → 生铁\n生铁 → 转炉炼钢 → 钢水 → 连铸轧钢 → 钢材',
    keyPoints: [
      '选矿是基础：将原矿品位从20-40%提升至65%以上',
      '高炉利用系数是核心指标：行业一般2.0-2.5 t/(m³·天)',
      '入炉焦比直接影响成本：标杆337 kg/t',
      '富氢碳循环高炉是低碳方向',
      '电炉炼钢碳排放仅为转炉的1/3-1/2',
    ],
    technicalPoints: [
      '原矿品位：TFe 20-40%',
      '精矿品位：TFe ≥65%',
      '高炉温度：1500-1600℃',
      '利用系数：2.0-2.5 t/(m³·天)',
      '入炉焦比：337-400 kg/t',
    ],
    notes: [
      '中国铁矿特点：贫矿多、品位低(33%)',
      '露天开采占比>70%',
      '电炉炼钢是低碳发展方向',
    ],
  },
  'copper-ore': {
    corePrinciple: '铜矿石(Cu 0.3-2%) → 浮选富集 → 铜精矿(Cu 20-30%)\n铜精矿 → 闪速熔炼 → 冰铜 → 吹炼 → 粗铜 → 电解精炼 → 阴极铜(≥99.99%)',
    keyPoints: [
      '浮选是核心：回收率85-95%',
      '闪速熔炼硫捕集率≥99%',
      '电解精炼电流密度200-350 A/m²',
      '湿法冶炼适合低品位氧化矿',
      '全流程铜回收率≥95%',
    ],
    technicalPoints: [
      '原矿品位：Cu 0.3-2%',
      '精矿品位：Cu 20-30%',
      '熔炼温度：1300-1450℃',
      '综合能耗：≤180 kg标煤/t',
      '阴极铜纯度：≥99.99%',
    ],
    notes: [
      '全球铜矿品位持续下降：1.45%→1.12%',
      '斑岩型铜矿占储量45.5%',
      '湿法冶炼占比15-20%',
    ],
  },
  'nickel-ore': {
    corePrinciple: '硫化镍矿：浮选 → 造锍熔炼 → 高镍锍 → 电解精炼 → 电镍\n红土镍矿：RKEF → 镍铁 或 HPAL → 硫酸镍',
    keyPoints: [
      '硫化镍矿可浮选富集至Ni 6-12%',
      '红土镍矿难选，直接冶炼',
      'RKEF镍铁Ni+Co 10-30%',
      'HPAL浸出温度245-270℃，压力4-5MPa',
      '红土镍矿产量快速上升',
    ],
    technicalPoints: [
      '硫化精矿：Ni 6-12%',
      'RKEF温度：1400-1600℃',
      'HPAL温度：245-270℃',
      'HPAL压力：4-5MPa',
      'Ni回收率：90-95%',
    ],
    notes: [
      '红土镍矿储量占60-70%',
      '伴生元素多，综合利用价值高',
      'HPAL适合生产电池级镍',
    ],
  },
  'aluminum-ore': {
    corePrinciple: '铝土矿 → 拜耳法溶出 → 氢氧化铝 → 焙烧 → 氧化铝\n氧化铝 → 霍尔-埃鲁电解(950-970℃) → 铝锭',
    keyPoints: [
      '铝硅比(A/S)决定工艺选择',
      '中国铝土矿A/S多数在4-7之间',
      '吨铝直流电耗12600-13500 kWh',
      '电流效率先进值>94.5%',
      '赤泥处置是重大环保难题',
    ],
    technicalPoints: [
      '铝硅比：A/S 4-7',
      '溶出温度：240-280℃',
      '电解温度：950-970℃',
      '直流电耗：12600-13500 kWh/t',
      '电流效率：>94.5%',
    ],
    notes: [
      '中国一水硬铝石型为主',
      '能耗高于国外主因',
      '总能耗约63 GJ/t-Al',
    ],
  },
  'cobalt-ore': {
    corePrinciple: '铜钴伴生矿 → 浮选 → 钴精矿 → 硫酸化焙烧 → 酸浸\n酸浸液 → 萃取分离 → 电积 → 电解钴(≥99.95%)',
    keyPoints: [
      '钴90%以上以伴生矿形式产出',
      '刚果(金)占全球资源70%+',
      '高压氧浸回收率≥97%',
      '全流程钴回收率≥95%',
      '新能源需求推动钴价上涨',
    ],
    technicalPoints: [
      '原矿品位：Co 0.1-1.5%',
      '焙烧温度：580-620℃',
      '高压浸出：190℃',
      '回收率：≥95%',
      '产品纯度：≥99.95%',
    ],
    notes: [
      '独立钴矿物极少',
      '铜钴矿占资源58%',
      '深海采矿是未来方向',
    ],
  },
  'gold-ore': {
    corePrinciple: '金矿石 → 重选/浮选 → 金精矿 → 氰化浸出\n氰化浸出液 → 炭吸附 → 解吸电积 → 金泥熔炼 → 金锭(≥99.99%)',
    keyPoints: [
      '氰化浸出是核心方法',
      'NaCN浓度300-600mg/L',
      '浸出时间24-48h',
      '全流程回收率80-95%',
      '堆浸适合低品位矿(≤1 g/t)',
    ],
    technicalPoints: [
      '原矿品位：1-15 g/t',
      '精矿品位：50-200 g/t',
      '熔炼温度：1060-1200℃',
      'NaCN浓度：300-600mg/L',
      '产品纯度：≥99.99%',
    ],
    notes: [
      '品位持续下降',
      '氰化物毒性管控严格',
      '无氰提金是发展方向',
    ],
  },
  'silver-ore': {
    corePrinciple: '银矿石 → 浮选 → 银精矿 → 火法熔炼 → 多尔合金\n多尔合金 → 帕克斯法/电解精炼 → 银锭(99.99%)',
    keyPoints: [
      '伴生银占产量99%',
      '工业品位100-120 g/t',
      '电解电流密度250-300 A/m²',
      '综合回收率99.2%',
      '独立银矿较少',
    ],
    technicalPoints: [
      '工业品位：100-120 g/t',
      '熔炼温度：~1200℃',
      '电解电流密度：250-300 A/m²',
      '产品纯度：99.99%',
      '回收率：>90%',
    ],
    notes: [
      '银多来自铅锌矿、铜矿伴生',
      '伴生依赖性强',
      '品位波动大',
    ],
  },
};

interface MiningProcessDetailProps {
  route: MiningRoute;
  onClose: () => void;
}

const MiningProcessDetail: React.FC<MiningProcessDetailProps> = ({ route, onClose }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const detailData = processDetailsMap[route.id];

  if (!detailData) {
    return (
      <Card sx={{ height: '100%', backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff' }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary">
            暂无该工艺的详细信息
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h5"
            sx={{ color: isDarkMode ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}
          >
            {route.name} - 工艺详情
          </Typography>
          <Tooltip title="关闭详情">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 2, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? '#ffb74d' : '#ff9800',
              mb: 1,
            }}
          >
            📌 核心原理
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
            >
              {detailData.corePrinciple}
            </Typography>
          </Paper>
        </Box>

        {detailData.technicalPoints && detailData.technicalPoints.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#81d4fa' : '#0288d1',
                mb: 1,
              }}
            >
              ⚙️ 技术参数
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {detailData.technicalPoints.map((point, index) => (
                <Chip
                  key={index}
                  label={point}
                  size="small"
                  sx={{
                    backgroundColor: isDarkMode ? '#0d47a1' : '#e3f2fd',
                    color: isDarkMode ? '#90caf9' : '#1976d2',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {detailData.keyPoints && detailData.keyPoints.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#ce93d8' : '#9c27b0',
                mb: 1,
              }}
            >
              🔑 关键技术点
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {detailData.keyPoints.map((point, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: isDarkMode ? '#4a148c' : '#f3e5f5',
                    borderRadius: 1,
                    borderLeft: '4px solid',
                    borderColor: isDarkMode ? '#ce93d8' : '#9c27b0',
                  }}
                >
                  <Typography variant="body2">{point}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {detailData.comparison && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#66bb6a' : '#2e7d32',
                mb: 1,
              }}
            >
              📊 {detailData.comparison.title}
            </Typography>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                backgroundColor: isDarkMode ? '#2a2a2a' : '#fafafa',
                border: '1px solid',
                borderColor: isDarkMode ? '#444444' : '#e0e0e0',
              }}
            >
              <Table size="small">
                <TableBody>
                  <TableRow sx={{ backgroundColor: isDarkMode ? '#1b5e20' : '#e8f5e9' }}>
                    {detailData.comparison.headers.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#66bb6a' : '#2e7d32',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                  {detailData.comparison.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {detailData.notes && detailData.notes.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#ffcc80' : '#ff9800',
                mb: 1,
              }}
            >
              💡 实践要点
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {detailData.notes.map((note, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    pl: 2,
                    borderLeft: '3px solid',
                    borderColor: isDarkMode ? '#ffcc80' : '#ff9800',
                  }}
                >
                  {note}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MiningProcessDetail;
